import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Col, Row, Input } from 'reactstrap';
import { ApplicationListingFilters } from '../../utils';
import { SelectOption } from '../../../../../../../../components/Select';
import { AsyncSelect } from '../../../../../../../../components/Select/async';
import { ApplicationFormTemplate } from '../../../../../../../../entities/v1/applicant_tracking/ApplicationFormTemplate';
import { Question } from '../../../../../../../../entities/v1/survey_engine/Question';
import { ApplicationFormTemplateService } from '../../../../../../../../services/v1/applicant_tracking/ApplicationFormTemplateService';

interface PropTypes {
  isLoading: boolean;
  filters: ApplicationListingFilters;
  setFilters: (filters: ApplicationListingFilters) => void;
}

function getAllQuestionsFromTemplates(
  templates: ApplicationFormTemplate[],
): Question[] {
  return templates.reduce((acc: Question[], template) => {
    const questions =
      template?.surveyTemplate?.sections?.reduce(
        (sectionAcc: Question[], section) => [
          ...sectionAcc,
          ...(section?.questions || []),
        ],
        [],
      ) || [];
    return [...acc, ...questions];
  }, []);
}

function mapQuestionsToSelectOptions(
  questions: Question[],
  selectedValue?: string,
): SelectOption[] {
  return questions
    .filter((question) => question?.fieldType !== null && question?.title)
    .map((question) => ({
      value: question.id.toString(),
      label: question.title,
      selected: selectedValue === question.id.toString(),
    }));
}

export function ApplicationFormFilter(props: PropTypes) {
  const [templates, setTemplates] = useState<ApplicationFormTemplate[]>([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(
    null,
  );
  const [componentIsLoading, setComponentIsLoading] = useState<boolean>(true);
  const [preloadedOptions, setPreloadedOptions] = useState<SelectOption[]>([]);

  // Store callback functions to resolve when templates load
  const optionsLoadCallbacks = useRef<
    { resolve: (options: SelectOption[]) => void }[]
  >([]);

  const questionTitle = props.filters?.applicationFormQuestionTitle || null;
  const answerValue = props.filters?.applicationFormAnswerValue || null;

  const fetchApplicationFormTemplates = useCallback(async () => {
    try {
      setComponentIsLoading(true);
      const response = await ApplicationFormTemplateService.list({
        expand: [
          'survey_template',
          'survey_template.sections',
          'survey_template.sections.questions',
        ],
      });

      if (response?.applicationFormTemplates) {
        const validTemplates = response.applicationFormTemplates.filter(
          (template: ApplicationFormTemplate) =>
            template?.id && template?.surveyTemplate?.name,
        );

        setTemplates(validTemplates);

        // Create options from templates and resolve any pending promises
        const allQuestions = getAllQuestionsFromTemplates(validTemplates);
        const validQuestions = allQuestions.filter(
          (q) => q?.fieldType !== null && q?.title,
        );
        const options = mapQuestionsToSelectOptions(validQuestions);

        // Resolve any pending requests for options
        optionsLoadCallbacks.current.forEach(({ resolve }) => {
          resolve([...options]);
        });
        optionsLoadCallbacks.current = [];
      }
    } catch (error) {
      setTemplates([]);

      // Resolve any pending requests with empty array
      optionsLoadCallbacks.current.forEach(({ resolve }) => {
        resolve([]);
      });
      optionsLoadCallbacks.current = [];
    } finally {
      setComponentIsLoading(false);
    }
  }, []);

  // Fetch templates on component mount
  useEffect(() => {
    fetchApplicationFormTemplates();
  }, [fetchApplicationFormTemplates]);

  // Preload options when templates load
  useEffect(() => {
    if (templates.length > 0) {
      const allQuestions = getAllQuestionsFromTemplates(templates);
      const validQuestions = allQuestions.filter(
        (q) => q?.fieldType !== null && q?.title,
      );
      const options = mapQuestionsToSelectOptions(validQuestions);
      setPreloadedOptions(options);
    }
  }, [templates]);

  // Update selectedQuestionId when filters change (for initial load or external changes)
  useEffect(() => {
    if (questionTitle) {
      // Find matching question in templates
      const allQuestions = getAllQuestionsFromTemplates(templates);
      const matchingQuestion = allQuestions.find(
        (q) => q.title === questionTitle,
      );
      if (matchingQuestion) {
        setSelectedQuestionId(matchingQuestion.id.toString());
      }
    } else {
      setSelectedQuestionId(null);
    }
  }, [questionTitle, templates]);

  // Handle question selection
  const handleQuestionChange = (option: SelectOption | null) => {
    if (!option) {
      setSelectedQuestionId(null);
      clearFilters();
      return;
    }

    setSelectedQuestionId(option.value);
    props.setFilters({
      ...props.filters,
      applicationFormQuestionTitle: option.label,
      applicationFormAnswerValue: answerValue, // Maintain existing answer
    });
  };

  const handleAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAnswerValue = e.target.value;
    props.setFilters({
      ...props.filters,
      applicationFormAnswerValue: newAnswerValue,
    });
  };

  const clearFilters = () => {
    props.setFilters({
      ...props.filters,
      applicationFormQuestionTitle: null,
      applicationFormAnswerValue: null,
    });
  };

  const loadQuestionOptions = async (
    inputValue: string,
  ): Promise<SelectOption[]> => {
    // If templates are loaded
    if (templates.length > 0) {
      const allQuestions = getAllQuestionsFromTemplates(templates);

      // Filter valid questions that have both fieldType and title
      const validQuestions = allQuestions.filter(
        (q) => q?.fieldType !== null && q?.title,
      );

      // If we have an input value, filter by it, otherwise return all valid questions
      const filteredQuestions = inputValue
        ? validQuestions.filter((q) =>
            q.title.toLowerCase().includes(inputValue.toLowerCase()),
          )
        : validQuestions;

      return mapQuestionsToSelectOptions(
        filteredQuestions,
        selectedQuestionId || undefined,
      );
    }

    // If we have preloaded options already (unlikely but just in case)
    if (preloadedOptions.length > 0) {
      return preloadedOptions;
    }

    // If templates are still loading, register a callback to be called when they're ready
    return new Promise<SelectOption[]>((resolve) => {
      optionsLoadCallbacks.current.push({ resolve });

      // Safety timeout
      setTimeout(() => {
        const index = optionsLoadCallbacks.current.findIndex(
          (callback) => callback.resolve === resolve,
        );

        if (index !== -1) {
          optionsLoadCallbacks.current.splice(index, 1);
          resolve([]);
        }
      }, 10000);
    });
  };

  const getSelectedQuestionOption = () => {
    if (!selectedQuestionId) return null;

    const allQuestions = getAllQuestionsFromTemplates(templates);
    const matchingQuestion = allQuestions.find(
      (q) => q.id.toString() === selectedQuestionId,
    );

    if (!matchingQuestion) return null;

    return {
      value: matchingQuestion.id.toString(),
      label: matchingQuestion.title,
    };
  };

  return (
    <Col xs='3' className='mt-3h'>
      <label>
        <h5 className=''>Application Form</h5>
      </label>
      <Row className='gx-3'>
        <Col xs='6'>
          <AsyncSelect
            loadOptions={loadQuestionOptions}
            selected={getSelectedQuestionOption()}
            disabled={props.isLoading || componentIsLoading}
            onChange={handleQuestionChange}
            placeholder='Select question'
            isSearchable={true}
            isClearable={true}
            isLoading={componentIsLoading}
          />
        </Col>
        <Col xs='6'>
          <Input
            type='text'
            value={answerValue || ''}
            onChange={handleAnswerChange}
            placeholder='Enter answer'
            disabled={props.isLoading || !questionTitle}
          />
        </Col>
      </Row>
    </Col>
  );
}
